import React, { Fragment, Component, PureComponent } from "react";
import styled, { css, keyframes } from "styled-components";
import ShadowHashlink from "../containers/ShadowHashlink";
import { findOptimalImage, loadStack } from "../containers/ProgressiveHelpers";
import { basename } from "path";

const Container = styled.div`
  --side-padding: 2.5em;
  --padding: var(--side-padding);
  --columns: 4;
  --rows: 2;
  font-size: 7.5em;
  --column-width: 3em;
  --column-height: 2em;
  display: grid;
  grid-template-columns: repeat(var(--columns), var(--column-width));
  grid-template-rows: repeat(var(--rows), var(--column-height));
  grid-auto-rows: 0em;
  box-sizing: border-box;
  justify-content: center;
  position: relative;
  margin: 0.5em;

  @media (max-width: 1600px) {
    --columns: 4;
  }

  @media (max-width: 1200px) {
    --columns: 3;
  }

  @media (max-width: 750px) {
    --columns: 2;
  }

  @media (max-width: 400px) {
    --columns: 2;
    font-size: 6em;
  }
`;

const Shadow = styled.div`
  transition: all 0.5s;
  width: 100%;
  height: 100%;
  opacity: ${props => (props.visible ? 1 : 0)};
  ${props => !props.visible && `visibility: hidden`};
  background-color: rgba(0, 0, 0, 0.55);
  left: 0;
  top: 0;
  position: fixed;
  z-index: 800;

  &:before {
    content: "";
    width: var(--scrollbar-width);
    height: 100vh;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 1000;
    background: linear-gradient(
      180deg,
      rgb(111, 54, 255, 0.5),
      rgba(87, 124, 255, 0.25)
    );
  }

  &:after {
    content: "△ Zum Schließen klicken △";
    position: fixed;
    font-size: 1.75em;
    width: 100%;
    font-family: Raleway;
    color: white;
    text-align: center;
    -webkit-transition: all 0.5s;
    transition: all 0.5s;
    pointer-events: none;
    bottom: 2.5em;
    text-shadow: 2px 2px 7.5px rgb(0, 0, 0);
  }
`;

const ShowcaseHull = styled.div`
  z-index: 900;
  pointer-events: all;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  position: fixed;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  visibility: hidden;
`;

const Showcase = styled(ShowcaseHull)`
  visibility: visible;
  --vector: ${props =>
    props.vector
      ? `${-1 * props.vector.x}px,
    ${-1 * props.vector.y}px`
      : `0px, 0px`};
  --min-scale: ${props =>
    props.minScaleFactor
      ? `${props.minScaleFactor.factorX},${props.minScaleFactor.factorY}`
      : `0,0`};

  ${props =>
    props.showcaseDisappear
      ? css`
          animation: ${zoomFrom} 0.5s forwards;
        `
      : css`
          animation: ${zoomTo} 0.5s ease-in-out forwards;
        `};

  &:before {
    content: "";
    width: 110%;
    height: 110%;
    background: url(${props => props.placeholder}),
      linear-gradient(135deg, #333332, black);
    background-size: cover;
    background-position: center center;
    display: block;
    flex-shrink: 0;
    filter: blur(15px);
  }

  &:after {
    content: "";
    width: 100%;
    height: 100%;
    background: url(${props => props.showcasedPhoto});
    background-size: cover;
    background-position: center center;
    position: absolute;
  }
`;

const entering = keyframes`
  from {
    transform: scale(0.5);
    opacity: 0;
  }

  50% {
    opacity: 0.75;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const Picture = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  padding: 1.5px;
  box-sizing: border-box;
  cursor: pointer;
  animation: ${entering} 0.75s forwards;
`;

const PlaceholderPadding = styled.div`
  padding: 1.5px;
  box-sizing: border-box;
`;

const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  &:after {
    display: block;
    content: "";
    width: 110%;
    height: 110%;
    flex-shrink: 0;
    filter: blur(15px);
    background-image: url(${props => props.src});
    background-size: cover;
    background-position: center center;
  }
`;

class LocalityPicture extends Component {
  constructor() {
    super();

    this.state = {
      width: 0
    };

    this.element = React.createRef();
  }

  calculateDimensions = () => {
    if (
      this.element.current === null ||
      this.state.width === this.element.current.clientWidth
    ) {
      return;
    }

    this.setState({
      width: this.element.current.clientWidth
    });
  };

  startTransition = () => {
    const boundingClientRect = this.element.current.getBoundingClientRect();
    const middlepointX = boundingClientRect.left + boundingClientRect.width / 2;
    const middlepointY = boundingClientRect.top + boundingClientRect.height / 2;

    const viewportCenterX = document.documentElement.clientWidth / 2;
    const viewportCenterY = document.documentElement.clientHeight / 2;

    const vectorX = viewportCenterX - middlepointX;
    const vectorY = viewportCenterY - middlepointY;
    // console.log(viewportCenterY, middlepointY);
    // console.log(vectorX, vectorY);
    // console.log(this.props.placeholder);

    const minScaleFactorX =
      boundingClientRect.width / document.documentElement.clientWidth;
    const minScaleFactorY =
      boundingClientRect.height / document.documentElement.clientHeight;

    const initialScrollOffset = document.documentElement.scrollTop;

    this.props.onToggle(
      { x: vectorX, y: vectorY, scrollTop: initialScrollOffset },
      { factorX: minScaleFactorX, factorY: minScaleFactorY },
      findOptimalImage(document.documentElement, this.props.picture),
      this.element.current.currentSrc !== undefined
        ? this.element.current.currentSrc
        : this.props.picture.placeholder
    );
  };

  componentDidMount = () => {
    window.addEventListener("resize", this.calculateDimensions);

    this.calculateDimensions();
  };

  componentWillUnmount() {
    window.removeEventListener("resize", this.calculateDimensions);
  }

  componentDidUpdate = () => {
    this.calculateDimensions();
  };

  render() {
    const { picture, ...filteredProps } = this.props;
    return typeof document !== "undefined" ? (
      <Picture
        ref={this.element}
        onClick={this.startTransition}
        src={
          this.state.width === 0
            ? picture.images[picture.images.length - 1].path
            : undefined
        }
        srcSet={this.state.width !== 0 ? picture.srcSet : undefined}
        sizes={this.state.width !== 0 ? `${this.state.width}px` : undefined}
        {...filteredProps}
      />
    ) : (
      <PlaceholderPadding>
        <Placeholder src={picture.placeholder} />
      </PlaceholderPadding>
    );
  }
}

const zoomTo = keyframes`
  from {
    transform: translate(var(--vector)) scale(var(--min-scale));
  }
  to {
    transform: scale(0.75);
    box-shadow: black 0px 0px 1em 0;
  }
`;

const zoomFrom = keyframes`
  from {
    transform: scale(0.75);
    box-shadow: black 0px 0px 1em 0;
  }

  to {
    transform: translate(var(--vector)) scale(var(--min-scale));
  }
`;

class Gallery extends Component {
  constructor() {
    super();

    this.state = {
      vector: undefined,
      minScaleFactor: undefined,
      showcasedPhoto: undefined,
      showcasedPhotoPlaceholder: undefined,
      showcaseDisappear: false
    };
  }

  onToggle = (vector, minScaleFactor, showcasedPhoto, placeholder) => {
    if (this.state.vector === undefined) {
      this.setState({
        vector: vector,
        minScaleFactor: minScaleFactor,
        showcasedPhoto: showcasedPhoto,
        showcasedPhotoPlaceholder: placeholder
      });

      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      const style = `
        --scrollbar-width: ${scrollbarWidth}px;
        overflow: hidden;
        margin-right: var(--scrollbar-width);
        pointer-events: none;
        touch-action: none;
      `;
      document.body.setAttribute("style", style);
      window.addEventListener("resize", this.abortShowcase);
      window.addEventListener("scroll", this.abortShowcase);
    } else {
      this.setState({
        showcaseDisappear: true
      });
    }
  };

  abortShowcase = () => {
    this.setState({
      showcaseDisappear: true,
      vector: undefined,
      minScaleFactor: undefined
    });
  };

  shouldDisappear = () => {
    if (this.state.showcaseDisappear) {
      this.setState({
        vector: undefined,
        minScaleFactor: undefined,
        showcasedPhoto: undefined,
        showcasedPhotoPlaceholder: undefined,
        showcaseDisappear: false
      });
      document.body.setAttribute("style", "");
      window.removeEventListener("resize", this.abortShowcase);
      window.removeEventListener("scroll", this.abortShowcase);
    }
  };

  render() {
    return (
      <Fragment>
        <Shadow visible={this.state.showcasedPhoto ? true : false} />
        {this.state.showcasedPhoto ? (
          <Showcase
            showcasedPhoto={this.state.showcasedPhoto}
            placeholder={this.state.showcasedPhotoPlaceholder}
            vector={
              this.state.showcaseDisappear && this.state.vector
                ? {
                    ...this.state.vector,
                    y:
                      this.state.vector.y +
                      (document.documentElement.scrollTop -
                        this.state.vector.scrollTop)
                  }
                : this.state.vector
            }
            minScaleFactor={this.state.minScaleFactor}
            onClick={this.onToggle}
            onAnimationEnd={this.shouldDisappear}
            showcaseDisappear={this.state.showcaseDisappear}
          />
        ) : (
          <ShowcaseHull />
        )}
        <Container>
          <ShadowHashlink offset="-25%" id="praxen" />
          {this.props.photos.map(photo => (
            <LocalityPicture
              key={photo.src}
              onToggle={this.onToggle}
              picture={photo}
            />
          ))}
        </Container>
      </Fragment>
    );
  }
}

export default props => {
  const galleries = ["secondary-locality", "primary-locality"].map(
    localityName => loadStack(props.cms[localityName]["gallery-images"])
  );

  return <Gallery photos={galleries[props.locality === 0 ? 0 : 1]} />;
};



// WEBPACK FOOTER //
// /opt/build/repo/src/segments/GallerySegment.js